import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Star, ShieldCheck, Clock, ArrowDown } from "lucide-react";
import { useLang } from "@/context/Lang";
import { CONTACT, IMAGES } from "@/data";

const EASE = [0.76, 0, 0.24, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);

  const seals = [
    { icon: ShieldCheck, label: t.hero.seals[0] },
    { icon: Star, label: t.hero.seals[1] },
    { icon: Clock, label: t.hero.seals[2] },
  ];

  return (
    <section id="top" ref={ref} className="relative min-h-screen bg-navy grain overflow-hidden" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMAGES.hero} alt="Beautifully renovated modern home interior by Seven Home Services in Winter Garden, FL" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 z-10 bg-gradient-to-r from-navy via-navy/85 to-navy/30" />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-navy via-transparent to-navy/40" />

      <div className="relative z-20 max-w-7xl mx-auto px-4 md:px-8 min-h-screen flex flex-col justify-center pt-24 pb-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="inline-flex items-center gap-2 self-start bg-white/10 border border-white/15 backdrop-blur-md rounded-full px-4 py-2 mb-6"
        >
          <ShieldCheck size={16} className="text-gold" />
          <span className="text-xs md:text-sm font-semibold text-white/90 tracking-wide">{t.hero.badge}</span>
        </motion.div>

        <h1 className="font-display font-black text-white text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.9] tracking-tighter max-w-5xl">
          <Line delay={0.35}>{t.hero.l1}</Line>
          <Line delay={0.45}><span className="text-orange">{t.hero.l2}</span></Line>
          <Line delay={0.55}>{t.hero.l3}</Line>
        </h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-6 max-w-xl text-base md:text-lg text-white/70 leading-relaxed"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-9 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-cta-quote"
            onClick={() => scrollTo("quote")}
            className="group inline-flex items-center justify-center gap-2 bg-orange hover:bg-[#1550C0] text-white font-bold text-base px-8 py-4 rounded-full transition-colors"
          >
            {t.hero.cta1}
            <ArrowDown size={18} className="group-hover:translate-y-1 transition-transform" />
          </button>
          <a
            href={CONTACT.phoneHref}
            data-testid="hero-cta-call"
            className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-md text-white font-bold text-base px-8 py-4 rounded-full transition-colors"
          >
            <Phone size={18} /> {t.hero.cta2}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
        >
          {seals.map((s) => (
            <div key={s.label} className="flex items-center gap-2 text-white/75">
              <s.icon size={18} className="text-gold" />
              <span className="text-sm font-semibold">{s.label}</span>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
